import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, CalendarDays, Settings, LogOut, Crown, Loader2, UserCheck, BellRing, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BUILD_ID } from "../buildInfo";
import LeadsTab from "./admin/LeadsTab";
import BookingsTab from "./admin/BookingsTab";
import AvailabilityTab from "./admin/AvailabilityTab";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-display">{label}</span>
        <Icon size={16} className="text-[#D4AF37]" />
      </div>
      <p className="font-display font-bold text-3xl text-white">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});

  useEffect(() => {
    if (!localStorage.getItem("ec_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then(() => {
        setChecking(false);
        loadStats();
      })
      .catch(() => {
        localStorage.removeItem("ec_admin_token");
        navigate("/admin/login");
      });
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("ec_admin_token");
    navigate("/admin/login");
  };

  const [testingEmail, setTestingEmail] = useState(false);
  const testEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/admin/test-email");
      if (data.ok) {
        toast.success(`Email enviado desde ${data.sender} a ${data.recipient}. ID: ${data.id}`, { duration: 8000 });
      } else {
        toast.error(`Fallo: ${data.error}`, { duration: 12000 });
        console.log("Diagnóstico email:", data);
      }
    } catch (e) {
      toast.error("No se pudo ejecutar la prueba de email.");
    } finally {
      setTestingEmail(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
      </div>
    );
  }

  const tabs = [
    { id: "leads", label: "Leads", icon: Users },
    { id: "bookings", label: "Reservas", icon: CalendarDays },
    { id: "availability", label: "Disponibilidad", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 bg-black/80 backdrop-blur-xl z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown size={18} className="text-[#D4AF37]" />
            <div className="font-display font-bold tracking-[0.2em] uppercase text-xs">
              JordyBT<span className="gold-text">_Fitness</span>
              <span className="hidden sm:inline text-white/40 tracking-[0.3em] ml-2">· Admin</span>
            </div>
            <span data-testid="build-id" className="text-[9px] text-white/30 font-mono tracking-tight ml-1 hidden md:inline">
              build: {BUILD_ID}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="admin-test-email" onClick={testEmail} disabled={testingEmail} className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] transition-colors disabled:opacity-50">
              {testingEmail ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Probar email
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] transition-colors">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total leads" value={stats.total_leads} />
            <StatCard icon={BellRing} label="Nuevos" value={stats.new_leads} />
            <StatCard icon={CalendarDays} label="Reservas" value={stats.confirmed_bookings} />
            <StatCard icon={UserCheck} label="Clientes" value={stats.clients} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-xs md:text-sm font-display uppercase tracking-wide border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsTab onChange={loadStats} />}
        {tab === "bookings" && <BookingsTab onChange={loadStats} />}
        {tab === "availability" && <AvailabilityTab />}
      </div>
    </div>
  );
}
